import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PaintingCard from './PaintingCard';
import Modal from '../ui/Modal';
import { categories, getPaintingsByCategory } from '../../data/paintings';
import useScrollReveal from '../../hooks/useScrollReveal';

const GalleryGrid = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedPainting, setSelectedPainting] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [headerRef, headerVisible] = useScrollReveal();
  const { t, i18n } = useTranslation();

  const filteredPaintings = useMemo(() =>
    getPaintingsByCategory(activeCategory),
    [activeCategory]
  );

  const openModal = (painting) => {
    setSelectedPainting(painting);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedPainting(null), 300);
  };

  const navigatePainting = (direction) => {
    if (!selectedPainting) return;
    const currentIndex = filteredPaintings.findIndex(p => p.id === selectedPainting.id);
    let newIndex;

    if (direction === 'next') {
      newIndex = currentIndex === filteredPaintings.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? filteredPaintings.length - 1 : currentIndex - 1;
    }

    setSelectedPainting(filteredPaintings[newIndex]);
  };

  // Category translations mapping
  const getCategoryName = (categoryId) => {
    const categoryMap = {
      'all': t('gallery.categories.all'),
      'landscape': t('gallery.categories.landscapes'),
      'portrait': t('gallery.categories.portraits'),
      'stilllife': t('gallery.categories.stillLife'),
      'urban': t('gallery.categories.urbanScenes'),
      'garden': t('gallery.categories.gardens'),
      'underwater': t('gallery.categories.underwater'),
    };
    return categoryMap[categoryId] || categoryId;
  };

  // Pluralization for works count
  const getWorksLabel = (count) => {
    if (i18n.language?.startsWith('en')) {
      return count === 1 ? t('gallery.work_one') : t('gallery.work_few');
    }
    // Russian pluralization
    if (count === 1) return t('gallery.work_one');
    if (count >= 2 && count <= 4) return t('gallery.work_few');
    return t('gallery.work_many');
  };

  return (
    <section className="min-h-screen py-24 px-6 lg:px-8 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 reveal ${headerVisible ? 'visible' : ''}`}
        >
          <span className="text-sm text-[#B8963E] tracking-widest uppercase mb-4 block">
            {t('gallery.label')}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6 text-[#3D3831]">
            {t('gallery.title')}
          </h1>
          <div className="divider-elegant mb-6" />
          <p className="text-[#8B7E6A] max-w-2xl mx-auto">
            {t('gallery.description')}
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-5 py-2.5 text-sm tracking-wide transition-all duration-300 ${
                activeCategory === category.id
                  ? 'bg-[#3D3831] text-[#FAF7F2]'
                  : 'bg-white text-[#5A5249] hover:bg-[#E8E2D9] border border-[#E8E2D9]'
              }`}
            >
              {getCategoryName(category.id)}
            </button>
          ))}
        </div>

        {/* Results Count */}
        <p className="text-center text-[#8B7E6A] text-sm mb-8">
          {filteredPaintings.length} {getWorksLabel(filteredPaintings.length)}
        </p>

        {/* Masonry Grid */}
        <div className="masonry">
          {filteredPaintings.map((painting, index) => (
            <PaintingCard
              key={painting.id}
              painting={painting}
              onClick={openModal}
              index={index}
            />
          ))}
        </div>

        {/* Empty State */}
        {filteredPaintings.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#8B7E6A] text-lg">
              {t('gallery.noWorks')}
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        painting={selectedPainting}
        onNext={() => navigatePainting('next')}
        onPrev={() => navigatePainting('prev')}
      />
    </section>
  );
};

export default GalleryGrid;
